import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { Check, MailCheck, ShieldAlert } from "lucide-react";
import { api, ApiError, clearCsrfToken } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Select } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Feedback";
import { Brand } from "./RegisterScreen";
import { cn } from "@/lib/cn";

const forgotSchema = z.looseObject({
  masked_email: z.string(),
  expires_at: z.string(),
});

const stateSchema = z.looseObject({
  pending: z.boolean(),
  masked_email: z.string().nullable().default(null),
  expires_at: z.string().nullable().default(null),
  attempts_remaining: z.number().default(5),
});

const resetSchema = z.looseObject({
  redirect: z.string().default("/patient/login"),
});

const PASSWORD_RULES: { label: string; test: (value: string) => boolean }[] = [
  { label: "Minimal 8 karakter", test: (v) => v.length >= 8 },
  { label: "Ada huruf besar", test: (v) => /[A-Z]/.test(v) },
  { label: "Ada huruf kecil", test: (v) => /[a-z]/.test(v) },
  { label: "Ada angka", test: (v) => /[0-9]/.test(v) },
];

/** mm:ss left before the code expires. */
function useCountdown(expiresAt: string | null) {
  const [left, setLeft] = useState(0);

  useEffect(() => {
    if (!expiresAt) return;
    const target = new Date(expiresAt).getTime();
    const tick = () => setLeft(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!expiresAt) return null;
  const minutes = String(Math.floor(left / 60)).padStart(2, "0");
  const seconds = String(left % 60).padStart(2, "0");
  return { left, label: `${minutes}:${seconds}` };
}

/**
 * Password recovery, both steps on one screen.
 *
 * The OTP is bound to the Flask session, not to a URL, so the code can only be
 * redeemed by the browser that requested it. That is also why the two steps
 * cannot be separate pages with their own entry points.
 */
export function PasswordRecoveryScreen() {
  const navigate = useNavigate();

  const [step, setStep] = useState<"request" | "verify">("request");
  const [accountType, setAccountType] = useState<"patient" | "admin">("patient");
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState(5);

  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const countdown = useCountdown(expiresAt);
  const expired = countdown != null && countdown.left === 0;
  const passwordOk = PASSWORD_RULES.every((rule) => rule.test(password));

  // A code may still be open from a previous visit; resume rather than
  // silently issuing a second one and invalidating the code already emailed.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/auth/password/reset-state", stateSchema)
      .then((state) => {
        if (cancelled || !state.pending) return;
        setStep("verify");
        setMaskedEmail(state.masked_email);
        setExpiresAt(state.expires_at);
        setAttemptsLeft(state.attempts_remaining);
      })
      .catch(() => {
        // No pending reset is the normal case, not an error.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function requestCode(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await api.post("/api/auth/password/forgot", forgotSchema, {
        email,
        account_type: accountType,
      });
      setMaskedEmail(result.masked_email);
      setExpiresAt(result.expires_at);
      setAttemptsLeft(5);
      setOtp("");
      setStep("verify");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Tidak dapat terhubung ke server.");
    } finally {
      setPending(false);
    }
  }

  async function submitReset(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await api.post("/api/auth/password/reset", resetSchema, {
        otp,
        password,
        password_confirmation: confirmation,
      });
      clearCsrfToken();
      navigate(result.redirect, { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError) {
        setError(cause.message);
        const payload = cause.payload as { attempts_remaining?: number } | null;
        if (typeof payload?.attempts_remaining === "number") {
          setAttemptsLeft(payload.attempts_remaining);
        }
        // An exhausted or expired code cannot be retried — send the user back
        // to request a fresh one instead of leaving a dead form on screen.
        if (["expired", "too_many_attempts", "no_pending_reset"].includes(cause.code ?? "")) {
          setStep("request");
          setExpiresAt(null);
        }
      } else {
        setError("Tidak dapat terhubung ke server.");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="bg-canvas grid min-h-dvh place-items-center px-5 py-10">
      <div className="w-full max-w-sm">
        <Brand />

        {step === "request" ? (
          <>
            <p className="eyebrow mb-1.5">Pemulihan akun</p>
            <h1 className="text-2xl">Lupa password</h1>
            <p className="text-content-muted mt-1.5 text-sm">
              Kami akan mengirim kode 6 digit ke email terdaftar Anda.
            </p>

            <form onSubmit={requestCode} className="mt-6 space-y-4" noValidate>
              {error && <Alert tone="danger">{error}</Alert>}

              <Select
                label="Jenis akun"
                value={accountType}
                onChange={(event) => setAccountType(event.target.value as "patient" | "admin")}
              >
                <option value="patient">Pasien</option>
                <option value="admin">Admin / staf</option>
              </Select>

              <Input
                label="Email terdaftar"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />

              <Button type="submit" variant="primary" fullWidth loading={pending}>
                Kirim kode
              </Button>

              <p className="text-content-muted text-center text-sm">
                <Link to="/patient/login" className="hover:text-content underline underline-offset-2">
                  Kembali ke halaman masuk
                </Link>
              </p>
            </form>
          </>
        ) : (
          <>
            <div className="bg-accent/10 mb-4 flex items-start gap-3 rounded-xl px-4 py-3">
              <MailCheck aria-hidden className="text-accent mt-0.5 size-4 shrink-0" />
              <p className="text-sm">
                Kode dikirim ke <b>{maskedEmail}</b>. Periksa juga folder spam.
              </p>
            </div>

            <p className="eyebrow mb-1.5">Langkah 2</p>
            <h1 className="text-2xl">Buat password baru</h1>

            <form onSubmit={submitReset} className="mt-5 space-y-4" noValidate>
              {error && <Alert tone="danger">{error}</Alert>}

              {expired && (
                <Alert tone="warning" title="Kode kedaluwarsa">
                  Minta kode baru untuk melanjutkan.
                </Alert>
              )}

              <Input
                label="Kode OTP"
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="tabular text-center text-lg tracking-[0.4em]"
                hint={
                  countdown && !expired
                    ? `Berlaku ${countdown.label} · sisa ${attemptsLeft} percobaan`
                    : undefined
                }
                required
              />

              <Input
                label="Password baru"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              <Input
                label="Ulangi password baru"
                type="password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="new-password"
                error={
                  confirmation && password !== confirmation
                    ? "Konfirmasi password tidak sama."
                    : undefined
                }
                required
              />

              <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
                {PASSWORD_RULES.map((rule) => {
                  const met = rule.test(password);
                  return (
                    <li
                      key={rule.label}
                      className={cn(
                        "inline-flex items-center gap-1.5 text-xs",
                        met ? "text-routine-text" : "text-content-subtle",
                      )}
                    >
                      <Check aria-hidden className={cn("size-3.5", !met && "opacity-30")} />
                      {rule.label}
                    </li>
                  );
                })}
              </ul>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={pending}
                disabled={otp.length !== 6 || !passwordOk || password !== confirmation || expired}
              >
                Simpan password baru
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep("request");
                  setError(null);
                  setExpiresAt(null);
                  setOtp("");
                }}
                className="text-content-muted hover:text-content w-full text-center text-sm underline underline-offset-2"
              >
                Kirim ulang ke email lain
              </button>
            </form>
          </>
        )}

        <p className="text-content-subtle mt-6 flex items-start gap-2 text-xs">
          <ShieldAlert aria-hidden className="mt-px size-3.5 shrink-0" />
          <span>
            Kode hanya berlaku di perangkat yang memintanya dan kedaluwarsa dalam 10 menit.
          </span>
        </p>
      </div>
    </main>
  );
}
